import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, FileText, CheckCircle, XCircle, Clock, Printer, Plus, Eye, Check, X, Barcode } from 'lucide-react';
import PageContainer from '../layout/PageContainer';

interface OrderItem {
  ingredient_id: number;
  name: string;
  unit: string;
  current_stock: number;
  required_quantity: number;
  order_quantity: number;
  supplier: string;
  unit_cost: number;
  total_cost: number;
  priority: 'urgent' | 'normal' | 'low';
}

interface PurchaseOrder {
  id?: number;
  order_date: string;
  delivery_date: string;
  items: OrderItem[];
  total_amount: number;
  status: 'draft' | 'approved' | 'completed' | 'cancelled';
  approved_by?: string;
  approved_at?: string;
  completed_at?: string;
  created_at?: string;
  notes?: string;
}

export const PurchaseOrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [newOrder, setNewOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'list' | 'new' | 'detail'>('list');
  const [daysAhead, setDaysAhead] = useState(3);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // JANスキャン機能
  const [janScanMode, setJanScanMode] = useState(false);
  const [scannedJan, setScannedJan] = useState('');
  const janInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchOrders();
  }, [filterStatus]);

  const fetchOrders = async () => {
    try {
      const url = filterStatus === 'all'
        ? '/api/purchase-order/list'
        : `/api/purchase-order/list?status=${filterStatus}`;

      const response = await fetch(url);
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      console.error('発注書一覧取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateNewOrder = async () => {
    try {
      const response = await fetch('/api/purchase-order/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days_ahead: daysAhead, auto_generate: false })
      });
      const data = await response.json();
      setNewOrder(data);
      setActiveTab('new');
    } catch (error) {
      console.error('発注書生成エラー:', error);
    }
  };

  const saveOrder = async () => {
    if (!newOrder) return;

    try {
      const response = await fetch('/api/purchase-order/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          days_ahead: daysAhead,
          auto_generate: true
        })
      });
      const data = await response.json();

      alert(`発注書 #${data.order_id} を作成しました`);
      setNewOrder(null);
      setActiveTab('list');
      fetchOrders();
    } catch (error) {
      console.error('発注書保存エラー:', error);
      alert('発注書の保存に失敗しました');
    }
  };

  const approveOrder = async (orderId: number) => {
    const approvedBy = prompt('承認者名を入力してください：');
    if (!approvedBy) return;

    try {
      const response = await fetch(`/api/purchase-order/${orderId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: approvedBy })
      });

      if (response.ok) {
        alert('発注書を承認しました');
        fetchOrders();
        if (selectedOrder?.id === orderId) {
          fetchOrderDetail(orderId);
        }
      }
    } catch (error) {
      console.error('発注書承認エラー:', error);
      alert('承認に失敗しました');
    }
  };

  const completeOrder = async (orderId: number) => {
    if (!confirm('この発注を完了しますか？在庫が自動更新されます。')) return;

    try {
      // 簡易実装：全アイテムを受領したものとして処理
      const order = orders.find(o => o.id === orderId);
      if (!order) return;

      const receivedItems = order.items.map(item => ({
        ingredient_id: item.ingredient_id,
        received_quantity: item.order_quantity
      }));

      const response = await fetch(`/api/purchase-order/${orderId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ received_items: receivedItems })
      });

      if (response.ok) {
        alert('発注を完了し、在庫を更新しました');
        fetchOrders();
        setSelectedOrder(null);
        setActiveTab('list');
      }
    } catch (error) {
      console.error('発注完了エラー:', error);
      alert('完了処理に失敗しました');
    }
  };

  const cancelOrder = async (orderId: number) => {
    const reason = prompt('キャンセル理由を入力してください：');
    if (!reason) return;

    try {
      const response = await fetch(`/api/purchase-order/${orderId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        alert('発注書をキャンセルしました');
        fetchOrders();
        setSelectedOrder(null);
        setActiveTab('list');
      }
    } catch (error) {
      console.error('発注キャンセルエラー:', error);
      alert('キャンセルに失敗しました');
    }
  };

  const fetchOrderDetail = async (orderId: number) => {
    try {
      const response = await fetch(`/api/purchase-order/${orderId}`);
      const data = await response.json();
      setSelectedOrder(data);
      setActiveTab('detail');
    } catch (error) {
      console.error('発注書詳細取得エラー:', error);
    }
  };

  const printOrder = (orderId: number) => {
    window.open(`/api/purchase-order/${orderId}/print`, '_blank');
  };

  // JANスキャンモード切替
  const toggleJanScanMode = () => {
    setJanScanMode(!janScanMode);
    setScannedJan('');
    // モード切替後にinputにフォーカス
    if (!janScanMode) {
      setTimeout(() => {
        janInputRef.current?.focus();
      }, 100);
    }
  };

  // JANコードスキャン処理（Enter キー対応）
  const handleJanScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !scannedJan.trim()) return;

    try {
      // JANコードから材料情報を取得（将来実装）
      console.log('JANコードスキャン:', scannedJan);
      alert(`JANコード: ${scannedJan}\n\n※ この機能は材料マスターにJANコードが登録されている場合に自動で商品を特定します。\n現在は手動で商品を選択してください。`);

      // スキャン後はクリア
      setScannedJan('');
    } catch (error) {
      console.error('JANスキャンエラー:', error);
      alert('商品が見つかりませんでした');
      setScannedJan('');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: Clock, label: '下書き' },
      approved: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, label: '承認済み' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: '完了' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'キャンセル' }
    };

    const badge = badges[status as keyof typeof badges] || badges.draft;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      normal: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };

    const labels = {
      urgent: '緊急',
      normal: '通常',
      low: '低'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[priority as keyof typeof colors]}`}>
        {labels[priority as keyof typeof labels]}
      </span>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">読み込み中...</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="w-8 h-8 text-green-600" />
            発注書管理システム
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleJanScanMode}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                janScanMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Barcode className="w-5 h-5" />
              JANスキャン
            </button>
            <button
              onClick={generateNewOrder}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              新規発注書作成
            </button>
          </div>
        </div>

        {/* JANスキャン入力エリア */}
        {janScanMode && (
          <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
            <div className="flex items-center gap-3">
              <Barcode className="w-6 h-6 text-blue-600" />
              <div className="flex-1">
                <label className="block text-sm font-medium text-blue-900 mb-1">
                  商品JANコードをスキャン
                </label>
                <input
                  ref={janInputRef}
                  type="text"
                  value={scannedJan}
                  onChange={(e) => setScannedJan(e.target.value)}
                  onKeyPress={handleJanScan}
                  placeholder="バーコードをスキャン、またはJANコードを入力してEnter"
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <p className="text-xs text-blue-700 mt-1">
                  💡 材料マスターにJANコードが登録されている商品を自動検索します
                </p>
              </div>
            </div>
          </div>
        )}

        {/* タブナビゲーション */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'list'
                ? 'border-b-2 border-green-600 text-green-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            発注書一覧
          </button>
          {newOrder && (
            <button
              onClick={() => setActiveTab('new')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'new'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              新規発注書
            </button>
          )}
          {selectedOrder && (
            <button
              onClick={() => setActiveTab('detail')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'detail'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              発注書詳細
            </button>
          )}
        </div>

        {/* 発注書一覧 */}
        {activeTab === 'list' && (
          <div>
            {/* フィルター */}
            <div className="mb-4 flex gap-2">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-3 py-1 rounded ${filterStatus === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-200'}`}
              >
                全て
              </button>
              <button
                onClick={() => setFilterStatus('draft')}
                className={`px-3 py-1 rounded ${filterStatus === 'draft' ? 'bg-gray-800 text-white' : 'bg-gray-200'}`}
              >
                下書き
              </button>
              <button
                onClick={() => setFilterStatus('approved')}
                className={`px-3 py-1 rounded ${filterStatus === 'approved' ? 'bg-gray-800 text-white' : 'bg-gray-200'}`}
              >
                承認済み
              </button>
              <button
                onClick={() => setFilterStatus('completed')}
                className={`px-3 py-1 rounded ${filterStatus === 'completed' ? 'bg-gray-800 text-white' : 'bg-gray-200'}`}
              >
                完了
              </button>
            </div>

            {/* 一覧テーブル */}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">発注番号</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">発注日</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">納品希望日</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">合計金額</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">アクション</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">#{order.id}</td>
                      <td className="px-4 py-2">{new Date(order.order_date).toLocaleDateString('ja-JP')}</td>
                      <td className="px-4 py-2">{new Date(order.delivery_date).toLocaleDateString('ja-JP')}</td>
                      <td className="px-4 py-2">{getStatusBadge(order.status)}</td>
                      <td className="px-4 py-2 text-right">¥{order.total_amount.toLocaleString()}</td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => fetchOrderDetail(order.id!)}
                            className="text-blue-600 hover:text-blue-800"
                            title="詳細"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => printOrder(order.id!)}
                            className="text-gray-600 hover:text-gray-800"
                            title="印刷"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          {order.status === 'draft' && (
                            <button
                              onClick={() => approveOrder(order.id!)}
                              className="text-green-600 hover:text-green-800"
                              title="承認"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {order.status === 'approved' && (
                            <button
                              onClick={() => completeOrder(order.id!)}
                              className="text-blue-600 hover:text-blue-800"
                              title="完了"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {(order.status === 'draft' || order.status === 'approved') && (
                            <button
                              onClick={() => cancelOrder(order.id!)}
                              className="text-red-600 hover:text-red-800"
                              title="キャンセル"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {orders.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>発注書がありません</p>
              </div>
            )}
          </div>
        )}

        {/* 新規発注書 */}
        {activeTab === 'new' && newOrder && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">予測日数</label>
                  <select
                    value={daysAhead}
                    onChange={(e) => setDaysAhead(parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value={1}>1日先</option>
                    <option value={3}>3日先</option>
                    <option value={5}>5日先</option>
                    <option value={7}>1週間先</option>
                  </select>
                </div>
                <button
                  onClick={generateNewOrder}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  再生成
                </button>
              </div>
              <button
                onClick={saveOrder}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <FileText className="w-5 h-5" />
                発注書を保存
              </button>
            </div>

            {/* サマリー */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">品目数</p>
                <p className="text-2xl font-bold text-blue-600">{newOrder.items.length} 品目</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">緊急品目</p>
                <p className="text-2xl font-bold text-red-600">
                  {newOrder.items.filter(i => i.priority === 'urgent').length} 品目
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">合計金額</p>
                <p className="text-2xl font-bold text-green-600">
                  ¥{newOrder.items.reduce((sum, item) => sum + item.total_cost, 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* アイテムリスト */}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">優先度</th>
                    <th className="px-4 py-2 text-left">品名</th>
                    <th className="px-4 py-2 text-right">現在庫</th>
                    <th className="px-4 py-2 text-right">必要数</th>
                    <th className="px-4 py-2 text-right">発注数</th>
                    <th className="px-4 py-2 text-left">単位</th>
                    <th className="px-4 py-2 text-left">仕入先</th>
                    <th className="px-4 py-2 text-right">金額</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {newOrder.items.map((item, index) => (
                    <tr key={index} className={item.priority === 'urgent' ? 'bg-red-50' : ''}>
                      <td className="px-4 py-2">{getPriorityBadge(item.priority)}</td>
                      <td className="px-4 py-2 font-medium">{item.name}</td>
                      <td className="px-4 py-2 text-right">{item.current_stock}</td>
                      <td className="px-4 py-2 text-right">{item.required_quantity}</td>
                      <td className="px-4 py-2 text-right font-bold">{item.order_quantity}</td>
                      <td className="px-4 py-2">{item.unit}</td>
                      <td className="px-4 py-2">{item.supplier}</td>
                      <td className="px-4 py-2 text-right">¥{item.total_cost.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 発注書詳細 */}
        {activeTab === 'detail' && selectedOrder && (
          <div>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">発注書 #{selectedOrder.id}</h2>
                  <p className="text-gray-600">
                    作成日: {new Date(selectedOrder.created_at!).toLocaleString('ja-JP')}
                  </p>
                </div>
                <div className="flex gap-2">
                  {getStatusBadge(selectedOrder.status)}
                  <button
                    onClick={() => printOrder(selectedOrder.id!)}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Printer className="w-5 h-5" />
                    印刷
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">発注日</p>
                  <p className="font-medium">{new Date(selectedOrder.order_date).toLocaleDateString('ja-JP')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">納品希望日</p>
                  <p className="font-medium">{new Date(selectedOrder.delivery_date).toLocaleDateString('ja-JP')}</p>
                </div>
                {selectedOrder.approved_by && (
                  <div>
                    <p className="text-sm text-gray-600">承認者</p>
                    <p className="font-medium">{selectedOrder.approved_by}</p>
                  </div>
                )}
                {selectedOrder.approved_at && (
                  <div>
                    <p className="text-sm text-gray-600">承認日時</p>
                    <p className="font-medium">{new Date(selectedOrder.approved_at).toLocaleString('ja-JP')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* アイテムリスト */}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">品名</th>
                    <th className="px-4 py-2 text-right">発注数</th>
                    <th className="px-4 py-2 text-left">単位</th>
                    <th className="px-4 py-2 text-left">仕入先</th>
                    <th className="px-4 py-2 text-right">単価</th>
                    <th className="px-4 py-2 text-right">金額</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {selectedOrder.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 font-medium">{item.name}</td>
                      <td className="px-4 py-2 text-right">{item.order_quantity}</td>
                      <td className="px-4 py-2">{item.unit}</td>
                      <td className="px-4 py-2">{item.supplier}</td>
                      <td className="px-4 py-2 text-right">¥{item.unit_cost.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">¥{item.total_cost.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-right font-semibold">合計金額:</td>
                    <td className="px-4 py-2 text-right font-bold text-lg">
                      ¥{selectedOrder.total_amount.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* アクションボタン */}
            <div className="mt-6 flex justify-end gap-2">
              {selectedOrder.status === 'draft' && (
                <>
                  <button
                    onClick={() => approveOrder(selectedOrder.id!)}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <Check className="w-5 h-5" />
                    承認する
                  </button>
                  <button
                    onClick={() => cancelOrder(selectedOrder.id!)}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    キャンセル
                  </button>
                </>
              )}
              {selectedOrder.status === 'approved' && (
                <>
                  <button
                    onClick={() => completeOrder(selectedOrder.id!)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    受領完了
                  </button>
                  <button
                    onClick={() => cancelOrder(selectedOrder.id!)}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    キャンセル
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default PurchaseOrderManagement;